import React, { useState, useEffect } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import {
  FileText,
  Download,
  Building,
  User,
  Package,
  Truck,
  Calculator,
  DollarSign,
  Eye,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

/**
 * PDF Export Modal
 * Configures and generates a PDF quote document
 */
const PDFExportModal = ({
  isOpen,
  onClose,
  quoteTypes = [],
  salesEntries = [],  // Individual items (not grouped)
  installationData = { 
    cosM2i: '123', 
    emoXM2: '0.29', 
    selloXM2: '2.62', 
    destaXM2: '1.99', 
    eppXM2: '0.57', 
    utiPercent: '11',
    adicional: '0'
  },
  pdrData = { quantity: '', unitValue: '' }
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Local installation state (editable copy)
  const [localInstallation, setLocalInstallation] = useState({
    cosM2i: installationData.cosM2i || '123',
    emoXM2: installationData.emoXM2 || '0.29',
    selloXM2: installationData.selloXM2 || '2.62',
    destaXM2: installationData.destaXM2 || '1.99',
    eppXM2: installationData.eppXM2 || '0.57',
    utiPercent: installationData.utiPercent || '11',
    adicional: installationData.adicional || '0'
  });
  
  // Form state
  const [formData, setFormData] = useState({
    quoteCode: '',
    targetCompany: '',
    attentionName: '',
    projectName: '',
    containerQuantity: 1,
    freightIncrement: 0
  });

  // Items from quote types or direct entries
  const [items, setItems] = useState([]);

  // Calculate totals
  const [totals, setTotals] = useState({
    subtotal: 0,
    igv: 0,
    total: 0,
    totalM2: 0,
    installationTotal: 0,
    pdrTotal: 0,
    grandTotal: 0
  });

  // Initialize items from salesEntries (individual) or quoteTypes (grouped)
  useEffect(() => {
    // Only run when modal opens or data changes significantly
    if (!isOpen) return;
    
    // Prefer salesEntries if available (individual items without grouping)
    if (salesEntries && salesEntries.length > 0) {
      const newItems = salesEntries.map((entry, idx) => ({
        id: entry.id || `entry-${idx}`,
        code: entry.item_code || entry.code || '',
        description: entry.description || entry.matched_type_name || 'Sin descripción',
        material: entry.material || '',
        series: entry.series || '',
        color: entry.color || '',
        dimensions: entry.dimensions || (entry.width_m && entry.height_m ? `${entry.width_m}m x ${entry.height_m}m` : ''),
        quantity: entry.quantity || 1,
        area_m2: entry.area_m2 || (entry.width_m && entry.height_m ? entry.width_m * entry.height_m : 0),
        width_m: entry.width_m || 0,
        height_m: entry.height_m || 0,
        unit_value: entry.db_cost_per_m2 || entry.cost_per_m2 || 0,
        total: entry.calculated_value || (entry.area_m2 || 0) * (entry.db_cost_per_m2 || 0) * (entry.quantity || 1),
        observations: entry.observations || '',
        image_data: entry.image_data || null
      }));
      setItems(newItems);
    } else if (quoteTypes && quoteTypes.length > 0) {
      // Fallback to quoteTypes (grouped)
      // area_m2 is PER UNIT (total_area / entries_count), not total
      const newItems = quoteTypes.map(type => {
        const entriesCount = type.entries_count || 1;
        const areaPerUnit = (type.total_area || 0) / entriesCount;
        const sideLength = Math.sqrt(areaPerUnit);
        return {
          id: type.id,
          code: type.name?.split(' ')[0] || type.id?.slice(-6) || 'ITEM',
          description: type.name || 'Sin descripción',
          material: type.material || '',
          series: type.series || '',
          color: type.color || '',
          dimensions: type.total_area ? `${sideLength.toFixed(2)}m x ${sideLength.toFixed(2)}m` : '',
          quantity: entriesCount,
          area_m2: areaPerUnit,  // Area PER UNIT, not total
          width_m: sideLength,
          height_m: sideLength,
          unit_value: type.average_cost_per_m2 || 0,
          total: entriesCount * (type.average_cost_per_m2 || 0) * areaPerUnit,
          observations: '',
          image_data: type.image_data || null
        };
      });
      setItems(newItems);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Calculate totals when items or additional data changes
  useEffect(() => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const igv = subtotal * 0.18;
    const total = subtotal + igv;
    // area_m2 is per unit, so multiply by quantity to get total m2
    const totalM2 = items.reduce((sum, item) => sum + ((item.area_m2 || 0) * (item.quantity || 1)), 0);
    
    // Installation calculation using full formula:
    // COS FIN M2 = cos_m2i + emo + sello + desta + epp
    // PU VENTA = COS FIN M2 * (1 + %UTI)
    const cosM2i = parseFloat(localInstallation.cosM2i) || 123;
    const emoXM2 = parseFloat(localInstallation.emoXM2) || 0.29;
    const selloXM2 = parseFloat(localInstallation.selloXM2) || 2.62;
    const destaXM2 = parseFloat(localInstallation.destaXM2) || 1.99;
    const eppXM2 = parseFloat(localInstallation.eppXM2) || 0.57;
    const utiPercent = parseFloat(localInstallation.utiPercent) || 11;
    const adicional = parseFloat(localInstallation.adicional) || 0;
    
    // Base cost per m2 (COS FIN M2)
    const baseCostM2 = cosM2i + emoXM2 + selloXM2 + destaXM2 + eppXM2;
    
    // Calculate installation for each item (VENTANA ALTA gets 300% UTI)
    let installationSubtotal = items.reduce((sum, item) => {
      const area = item.area_m2 || 0;
      const qty = item.quantity || 1;
      const description = (item.description || '').toUpperCase();
      const itemUtiPercent = description.includes('VENTANA ALTA') ? 300 : utiPercent;
      const unitPricePerM2 = baseCostM2 * (1 + itemUtiPercent / 100);
      return sum + (area * unitPricePerM2 * qty);
    }, 0);
    
    // Add adicional to installation total
    const installationTotal = installationSubtotal + adicional;
    
    // PDR calculation
    const pdrQty = parseInt(pdrData.quantity) || 0;
    const pdrUnit = parseFloat(pdrData.unitValue) || 0;
    const pdrTotal = pdrQty * pdrUnit;
    
    const grandTotal = total + parseFloat(formData.freightIncrement || 0) + installationTotal;
    
    setTotals({
      subtotal,
      igv,
      total,
      totalM2,
      installationTotal,
      installationSubtotal,
      pdrTotal,
      grandTotal
    });
  }, [items, formData.freightIncrement, localInstallation, pdrData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate total
    if (field === 'quantity' || field === 'unit_value') {
      const qty = field === 'quantity' ? parseFloat(value) : newItems[index].quantity;
      const unit = field === 'unit_value' ? parseFloat(value) : newItems[index].unit_value;
      newItems[index].total = (qty || 0) * (unit || 0);
    }
    
    setItems(newItems);
  };

  const handleGenerate = async (downloadAfterPreview = false) => {
    // Validation
    if (!formData.quoteCode.trim()) {
      toast.error("Ingresa el código de cotización");
      return;
    }
    if (!formData.targetCompany.trim()) {
      toast.error("Ingresa la empresa destino");
      return;
    }
    if (items.length === 0) {
      toast.error("No hay elementos para exportar");
      return;
    }

    setIsGenerating(true);

    try {
      // Prepare request
      const requestData = {
        quote_code: formData.quoteCode,
        target_company: formData.targetCompany,
        attention_name: formData.attentionName,
        project_name: formData.projectName,
        items: items.map(item => ({
          code: item.code || '',
          description: item.description,
          series: item.series || '',
          dimensions: item.dimensions || '',
          quantity: item.quantity || 1,
          color: item.color || '',
          observations: item.observations || '',
          unit_value: item.unit_value || 0,
          total: item.total || 0,
          image_data: item.image_data || null,
          // Add dimensions for installation calculation
          width_m: item.width_m || 0,
          height_m: item.height_m || 0,
          area_m2: item.area_m2 || 0
        })),
        container_quantity: parseInt(formData.containerQuantity) || 1,
        freight_increment: parseFloat(formData.freightIncrement) || 0,
        total_m2: totals.totalM2
      };

      // Add installation if present - include all cost components
      const cosM2i = parseFloat(localInstallation.cosM2i) || 123;
      if (cosM2i > 0) {
        requestData.installation = {
          cos_m2i: cosM2i,
          emo_x_m2: parseFloat(localInstallation.emoXM2) || 0.29,
          sello_x_m2: parseFloat(localInstallation.selloXM2) || 2.62,
          desta_x_m2: parseFloat(localInstallation.destaXM2) || 1.99,
          epp_x_m2: parseFloat(localInstallation.eppXM2) || 0.57,
          uti_percent: parseFloat(localInstallation.utiPercent) || 11,
          adicional: parseFloat(localInstallation.adicional) || 0
        };
      }

      // Add PDR if present
      if (totals.pdrTotal > 0) {
        requestData.pdr = {
          quantity: parseInt(pdrData.quantity) || 1,
          unit_value: parseFloat(pdrData.unitValue) || 0,
          total: totals.pdrTotal
        };
      }

      // Generate PDF
      const response = await api.post(`${API}/sales/generate-pdf`, requestData, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (downloadAfterPreview) {
        // Download directly
        const link = document.createElement('a');
        link.href = url;
        link.download = `COTIZACION_${formData.quoteCode.replace(/\//g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("PDF descargado correctamente");
        setShowPreview(false);
        setPreviewUrl(null);
        onClose();
      } else {
        // Show preview
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(url);
        setShowPreview(true);
      }

    } catch (error) {
      // console.error("Error generating PDF:", error);
      toast.error(error.response?.data?.detail || "Error al generar PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setShowPreview(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Exportar Cotización PDF
          </DialogTitle>
          <DialogDescription>
            Configura los datos de la cotización y genera un PDF profesional
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2" style={{maxHeight: 'calc(90vh - 180px)'}}>
          <div className="space-y-6">
            {/* Quote Info Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Información de la Cotización
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quoteCode">Código de Cotización *</Label>
                    <Input
                      id="quoteCode"
                      value={formData.quoteCode}
                      onChange={(e) => handleInputChange('quoteCode', e.target.value)}
                      placeholder="MV-0010926"
                      data-testid="pdf-quote-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="targetCompany">Empresa Destino *</Label>
                    <Input
                      id="targetCompany"
                      value={formData.targetCompany}
                      onChange={(e) => handleInputChange('targetCompany', e.target.value)}
                      placeholder="SAN CHARBEL S.A.C."
                      data-testid="pdf-target-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="attentionName">Atención</Label>
                    <Input
                      id="attentionName"
                      value={formData.attentionName}
                      onChange={(e) => handleInputChange('attentionName', e.target.value)}
                      placeholder="Ing. Carlos Pérez"
                      data-testid="pdf-attention-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="projectName">Nombre del Proyecto</Label>
                    <Input
                      id="projectName"
                      value={formData.projectName}
                      onChange={(e) => handleInputChange('projectName', e.target.value)}
                      placeholder="PROYECTO RESIDENCIAL"
                      data-testid="pdf-project-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="containerQuantity">Cantidad de Contenedores</Label>
                    <Input
                      id="containerQuantity"
                      type="number"
                      min="1"
                      value={formData.containerQuantity}
                      onChange={(e) => handleInputChange('containerQuantity', e.target.value)}
                      data-testid="pdf-container-qty"
                    />
                  </div>
                  <div>
                    <Label>Total m² Calculados</Label>
                    <div className="h-10 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md flex items-center">
                      <span className="text-lg font-semibold text-blue-700">
                        {totals.totalM2.toFixed(2)} m²
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="freightIncrement">Incremento Flete ($)</Label>
                    <Input
                      id="freightIncrement"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.freightIncrement}
                      onChange={(e) => handleInputChange('freightIncrement', e.target.value)}
                      placeholder="0.00"
                      data-testid="pdf-freight"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Installation Configuration Card */}
            <Card className="border-green-200">
              <CardHeader className="pb-3 bg-green-50/50">
                <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                  <Package className="w-4 h-4" />
                  Configuración de Instalación
                </CardTitle>
                <CardDescription>
                  Parámetros para calcular el costo de instalación
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-7 gap-2">
                  <div>
                    <Label className="text-xs">COS M2i</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={localInstallation.cosM2i}
                      onChange={(e) => setLocalInstallation({...localInstallation, cosM2i: e.target.value})}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">EMO X M2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={localInstallation.emoXM2}
                      onChange={(e) => setLocalInstallation({...localInstallation, emoXM2: e.target.value})}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">SELLO X M2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={localInstallation.selloXM2}
                      onChange={(e) => setLocalInstallation({...localInstallation, selloXM2: e.target.value})}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">DESTA X M2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={localInstallation.destaXM2}
                      onChange={(e) => setLocalInstallation({...localInstallation, destaXM2: e.target.value})}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">EPP X M2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={localInstallation.eppXM2}
                      onChange={(e) => setLocalInstallation({...localInstallation, eppXM2: e.target.value})}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">% UTI</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={localInstallation.utiPercent}
                      onChange={(e) => setLocalInstallation({...localInstallation, utiPercent: e.target.value})}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-600 font-semibold">ADICIONAL</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={localInstallation.adicional}
                      onChange={(e) => setLocalInstallation({...localInstallation, adicional: e.target.value})}
                      className="h-8 text-xs border-orange-300 bg-orange-50"
                      placeholder="5000.00"
                      data-testid="pdf-adicional"
                    />
                  </div>
                </div>
                <div className="mt-3 p-2 bg-green-50 rounded border border-green-200 text-xs">
                  <div className="flex justify-between">
                    <span>COS FIN M2 (base):</span>
                    <span className="font-mono">
                      S/ {(
                        (parseFloat(localInstallation.cosM2i) || 123) +
                        (parseFloat(localInstallation.emoXM2) || 0.29) +
                        (parseFloat(localInstallation.selloXM2) || 2.62) +
                        (parseFloat(localInstallation.destaXM2) || 1.99) +
                        (parseFloat(localInstallation.eppXM2) || 0.57)
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700 mt-1">
                    <span>Total Instalación (con {localInstallation.utiPercent || 11}% UTI + Adicional):</span>
                    <span className="font-mono">S/ {totals.installationTotal.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Items Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Elementos ({items.length})
                </CardTitle>
                <CardDescription>
                  Área total: {totals.totalM2.toFixed(2)} m²
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-auto max-h-64">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white">
                      <TableRow>
                        <TableHead className="w-12">Img</TableHead>
                        <TableHead className="w-20">Código</TableHead>
                        <TableHead className="w-[130px]">Descripción</TableHead>
                        <TableHead className="w-16">Serie</TableHead>
                        <TableHead className="w-20">Dimensiones</TableHead>
                        <TableHead className="w-12 text-center">Cant.</TableHead>
                        <TableHead className="w-16">Color</TableHead>
                        <TableHead className="w-[90px]">Observaciones</TableHead>
                        <TableHead className="w-18 text-right">$/m²</TableHead>
                        <TableHead className="w-22 text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={item.id || index}>
                          <TableCell className="p-1">
                            {item.image_data ? (
                              <img 
                                src={item.image_data} 
                                alt="" 
                                className="w-8 h-8 object-contain rounded"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                                <FileText className="w-4 h-4 text-slate-400" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.code || ''}
                              onChange={(e) => handleItemChange(index, 'code', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="Código"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              className="h-7 text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.series || ''}
                              onChange={(e) => handleItemChange(index, 'series', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="Serie"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.dimensions || ''}
                              onChange={(e) => handleItemChange(index, 'dimensions', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="0.00x0.00"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                              className="h-7 text-xs text-center"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.color || ''}
                              onChange={(e) => handleItemChange(index, 'color', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="Color"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.observations || ''}
                              onChange={(e) => handleItemChange(index, 'observations', e.target.value)}
                              className="h-7 text-xs"
                              placeholder="Obs..."
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_value}
                              onChange={(e) => handleItemChange(index, 'unit_value', parseFloat(e.target.value) || 0)}
                              className="h-7 text-xs text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            {formatCurrency(item.total || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Totals Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Resumen de Totales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>IGV (18%):</span>
                    <span>{formatCurrency(totals.igv)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Total Suministro:</span>
                    <span>{formatCurrency(totals.total)}</span>
                  </div>
                  {parseFloat(formData.freightIncrement) > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Flete:</span>
                      <span>{formatCurrency(parseFloat(formData.freightIncrement))}</span>
                    </div>
                  )}
                  {totals.installationTotal > 0 && (
                    <div className="flex flex-col gap-1 text-green-600 border-t pt-2 mt-2">
                      <div className="flex justify-between text-xs">
                        <span>Base instalación:</span>
                        <span>S/ {(totals.installationSubtotal || 0).toFixed(2)}</span>
                      </div>
                      {parseFloat(installationData.adicional) > 0 && (
                        <div className="flex justify-between text-xs">
                          <span>Adicional:</span>
                          <span>S/ {parseFloat(installationData.adicional).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-medium">
                        <span>Total Instalación:</span>
                        <span>S/ {totals.installationTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  {totals.pdrTotal > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>PDR:</span>
                      <span>S/ {totals.pdrTotal.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>TOTAL GENERAL:</span>
                    <span className="text-blue-600">{formatCurrency(totals.grandTotal)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="mt-4 pt-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            variant="secondary"
            onClick={() => handleGenerate(false)} 
            disabled={isGenerating}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            {isGenerating && !showPreview ? "Generando..." : "Vista Previa"}
          </Button>
          <Button 
            onClick={() => handleGenerate(true)} 
            disabled={isGenerating}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            {isGenerating && showPreview ? "Descargando..." : "Descargar PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* PDF Preview Popup */}
      <Dialog open={showPreview} onOpenChange={handleClosePreview}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                Vista Previa - {formData.quoteCode || "Cotización"}
              </DialogTitle>
              <div className="flex gap-2">
                <Button 
                  size="sm"
                  onClick={() => handleGenerate(true)} 
                  disabled={isGenerating}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Descargar
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={handleClosePreview}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 h-[80vh]">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default PDFExportModal;
